import { CONTENT_LOCALE_OVERRIDES } from "@/lib/content/locales";
import { applyLocaleOverrides } from "@/lib/content/locales/apply-locale-overrides";
import { DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import { resolveContent } from "../resolve";
import { defineContentEntry, staticText, text } from "../schema";

const sourceFile = "lib/content/entries/funnel-planner.ts";

const baseFunnelPlannerEntry = defineContentEntry({
  key: "funnel-planner",
  routeKey: "funnel-planner",
  routePath: "/funnel-planner",
  templateFamily: "service",
  baseLocale: DEFAULT_LOCALE,
  ownership: {
    owner: "content-registry",
    sourceFile,
    migratedFrom: [],
    notes: "Funnel planner tool content entry.",
  },
  schemaTypes: ["WebPage"],
  requiredFields: [
    { path: "metadataTitle", kind: "metadata", seoCritical: true },
    { path: "metadataDescription", kind: "metadata", seoCritical: true },
    { path: "step1Title", kind: "h1", seoCritical: false },
    { path: "getMyPlanButton", kind: "cta" },
  ],
  seoCriticalFields: ["metadataTitle", "metadataDescription"],
  indexability: {
    indexableByDefault: true,
    allowEnglishPreviewFallback: false,
  },
  base: {
    metadataTitle: text("Funnel Planner — Map Your SEO Revenue Path | SEOH"),
    metadataDescription: text("Build a custom SEO funnel in 7 steps. Map stages, model regional projections, compare scenarios, and download a branded PDF plan."),
    staticAnswerEyebrow: text("Funnel Planner"),
    staticAnswerTitle: text("SEO funnel planner for revenue-stage mapping"),
    staticAnswerIntro: text("Use the planner to choose a funnel type, map traffic channels, define offers, set audience goals, build stages, model projections, and export a branded plan."),
    staticStepsTitle: text("What the planner covers"),
    gamificationTierOneEyebrow: text("Guided planner layer"),
    gamificationTierOneTitle: text("Turn the planner into a calm implementation brief"),
    gamificationTierOneIntro: text("These checks mirror the audit-approved Tier 1 mechanics. They use planner progress bands only and do not change the CRM handoff."),
    gamificationStatusReady: text("Ready"),
    gamificationStatusReview: text("Review"),
    gamificationStatusCurrent: text("Current"),
    gamificationStatusWaiting: text("Waiting"),
    gamificationActionReview: text("Review"),
    gamificationActionReviewNextGap: text("Review next gap"),
    gamificationReadinessBoardTitle: text("Readiness board"),
    gamificationReadinessBoardIntro: text("Follow the planner state from draft to handoff without points, streaks, or pressure."),
    gamificationReadinessProgress: text("{ready} of {total} checks ready"),
    gamificationReadinessFocusLabel: text("Planner readiness checks"),
    gamificationReadinessTemplateLabel: text("Funnel type selected"),
    gamificationReadinessTemplateHelp: text("Start with the closest template so later assumptions stay grounded."),
    gamificationReadinessTrafficLabel: text("Traffic inputs mapped"),
    gamificationReadinessTrafficHelp: text("At least one channel needs planned volume before the model is useful."),
    gamificationReadinessOfferLabel: text("Offer stack defined"),
    gamificationReadinessOfferHelp: text("Add the core offer before trusting pricing and projection signals."),
    gamificationReadinessAudienceLabel: text("Audience and goal captured"),
    gamificationReadinessAudienceHelp: text("Keep the goal in the planner and send only safe summary fields to analytics."),
    gamificationReadinessStagesLabel: text("Funnel stages mapped"),
    gamificationReadinessStagesHelp: text("The stage map turns the plan from a template into a reviewable journey."),
    gamificationReadinessProjectionLabel: text("Projection reviewed"),
    gamificationReadinessProjectionHelp: text("Review model health before treating the brief as implementation-ready."),
    gamificationReadinessHandoffLabel: text("Handoff ready"),
    gamificationReadinessHandoffHelp: text("Submit only after the plan, consent, and export path are clear."),
    gamificationPreviewReadinessTitle: text("Planner readiness board"),
    gamificationPreviewReadinessBody: text("Inputs, handoff, and projection checks for the current planner draft."),
    gamificationPreviewReadinessProgress: text("{ready} of {total} ready"),
    gamificationPreviewReadinessTierLabel: text("Readiness tier"),
    gamificationPreviewReadinessBlockersLabel: text("Fix next"),
    gamificationPreviewReadinessNoBlockers: text("No blockers marked."),
    gamificationPreviewReadinessStatusSelect: text("{item} status"),
    gamificationPreviewReadinessStatusUntouched: text("Untouched"),
    gamificationPreviewReadinessStatusReview: text("In review"),
    gamificationPreviewReadinessLiveSummary: text("{ready} of {total} checks ready. Tier: {tier}. Next: {next}."),
    gamificationPreviewReadinessTierDraft: text("Draft"),
    gamificationPreviewReadinessTierDraftPlus: text("Draft plus"),
    gamificationPreviewReadinessTierReviewReady: text("Review ready"),
    gamificationPreviewReadinessTierHandoffReady: text("Handoff ready"),
    gamificationProjectionKpisReady: text("Projection KPI real-number count-up ready"),
    gamificationOfferAssemblyTitle: text("Offer assembly mini"),
    gamificationOfferAssemblyIntro: text("The CTA becomes a brief only when the planner has enough safe pieces to explain the next step."),
    gamificationOfferAssemblyPiecesLabel: text("Brief pieces"),
    gamificationOfferAssemblyComplete: text("The brief has enough structure for a human review."),
    gamificationOfferAssemblyIncomplete: text("The brief is still missing one or more planner pieces."),
    gamificationOfferAssemblyCtaEyebrow: text("Planner action"),
    gamificationOfferAssemblyCtaTitle: text("Review focus"),
    gamificationOfferAssemblyCtaProgress: text("{selected} of {total} sections selected"),
    gamificationOfferAssemblyCtaEmptyStatus: text("Choose the planner sections to review first."),
    gamificationOfferAssemblyCtaPartialStatus: text("Choose one more section before marking a review focus."),
    gamificationOfferAssemblyCtaReadyStatus: text("Review focus: {scopeBand}. Next planner piece: {piece}."),
    gamificationOfferAssemblyCtaOpenBuilder: text("Choose sections"),
    gamificationOfferAssemblyCtaScopeThis: text("Mark for review"),
    gamificationOfferAssemblyCtaDisabled: text("Choose 2 sections"),
    gamificationOfferAssemblyCtaActivated: text("Section picker opened. {selected} of {total} sections are selected."),
    gamificationOfferAssemblyCtaExpanded: text("Review sections are visible."),
    gamificationOfferAssemblyCtaCollapsed: text("Review sections are hidden."),
    gamificationOfferAssemblyCtaSelectedStatus: text("{selected} sections selected. Review focus: {scopeBand}."),
    gamificationOfferAssemblyCtaScopedStatus: text("Review focus saved. Continue with {piece}."),
    gamificationOfferAssemblyCtaShowDetails: text("Choose sections"),
    gamificationOfferAssemblyCtaHideDetails: text("Close"),
    gamificationOfferAssemblyCtaPiecesAria: text("Planner sections to review"),
    gamificationOfferAssemblyCtaSelected: text("Selected"),
    gamificationRevealReadingTitle: text("Before and after AI-answer reveal"),
    gamificationRevealReadingIntro: text("Compare generic advice with the planner-aware answer the page can support."),
    gamificationRevealSectionTitle: text("Section answer reveal"),
    gamificationRevealSectionIntro: text("Use the reveal to see how static page context turns into a handoff-ready section."),
    gamificationRevealBeforeLabel: text("Before"),
    gamificationRevealAfterLabel: text("After"),
    gamificationRevealSliderLabel: text("Switch answer view"),
    gamificationRevealReadingBefore: text("Generic AI advice says to build a funnel, choose channels, and review metrics."),
    gamificationRevealReadingAfter: text("This plan can connect the selected template, channel state, offer stack, and next blocker into one review path."),
    gamificationRevealSectionBefore: text("The static page explains the seven planner steps."),
    gamificationRevealSectionAfter: text("The current planner state highlights what is ready, what needs review, and where the next useful click belongs."),
    gamificationPricingConfiguratorTitle: text("Offer assembly configurator"),
    gamificationPricingConfiguratorIntro: text("Review the pricing role mix as bands and counts, not raw sensitive commercial detail."),
    gamificationPricingRoleLabel: text("Offer role"),
    gamificationPricingRoleCore: text("Core offer"),
    gamificationPricingRoleOrderBump: text("Order bump"),
    gamificationPricingRoleUpsell: text("Upsell"),
    gamificationPricingRoleContinuity: text("Continuity"),
    gamificationPricingRoleReady: text("This role is represented in the current offer stack."),
    gamificationPricingRoleMissing: text("This role is not represented yet. Review only if it fits the business model."),
    gamificationPricingNoProducts: text("Add a core offer before using pricing recommendations."),
    gamificationPricingProductsCount: text("{count} offer items in the current stack"),
    gamificationPricingProjectedBand: text("Projected band"),
    gamificationPricingTrafficBand: text("Traffic band"),
    gamificationFitChecklistTitle: text("Does this fit you?"),
    gamificationFitChecklistIntro: text("Use this self-check to decide whether the planner output is worth sending for review."),
    gamificationFitChecklistProgress: text("{checked} of {total} fit checks selected"),
    gamificationFitChecklistBuyer: text("The buyer or client segment is clear enough to brief."),
    gamificationFitChecklistChannel: text("The first traffic channel has a believable source."),
    gamificationFitChecklistOffer: text("The core offer can be described without private notes."),
    gamificationFitChecklistModel: text("The projection can be reviewed before any execution work."),
    gamificationFitChecklistReady: text("This looks ready for a planner review."),
    gamificationFitChecklistDraft: text("Keep this in draft until the missing checks are clear."),

    step1Title: text("Your funnel type shapes every step ahead — let us get it right"),
    step1Subtitle: text("Get your custom funnel plan + PDF export — takes about 3 minutes"),
    step2Title: text("Select your traffic channels"),
    step2Subtitle: text("Choose the channels that drive visitors into your funnel and set monthly volumes"),
    step3Title: text("Your Offers & Products"),
    step3Subtitle: text("Define what you sell so we can model your exact revenue, P&L, and KPIs"),
    step4Title: text("Define your audience & goal"),
    step4Subtitle: text("Tell us who you're targeting and what you want to achieve"),
    step5Title: text("Build your funnel stages"),
    step5Subtitle: text("Add, remove, and adjust each stage to match your sales process"),
    step6Title: text("Model your projections"),
    step6Subtitle: text("Set your horizon and review monthly revenue, P&L, and leverage analysis."),
    step7Title: text("Get your funnel plan"),
    step7Subtitle: text("Review your handoff details and download the branded PDF. This step saves the plan only - it does not send email automatically."),

    nextButton: text("Next"),
    backButton: text("Back"),
    getMyPlanButton: text("Download My Funnel Plan"),
    downloadingButton: text("Generating your plan…"),

    templatePickerLabel: text("Each template pre-configures your funnel stages, KPIs, and traffic targets — you can adjust later"),
    templatePickerHelp: text("Your quiz answers have been used to highlight the best match for you below."),
    templatePickerHelpDirect: text("Pick the model closest to your business — you can rename and add stages in step 5."),
    fitQuizAcknowledgment: text("Your quiz answers matched you to a recommended template — you are already halfway there!"),
    recommendedBadge: text("Recommended"),
    popularBadge: text("Popular"),
    bestForPrefix: text("Best for:"),
    selectTemplateHint: text("Select a template to continue"),
    selectTemplateToAdvance: text("Select a funnel template above to continue"),

    "templates.agency-white-label.label": text("White-Label Agency"),
    "templates.agency-white-label.bestFor": text("Agencies reselling SEO services"),
    "templates.agency-white-label.description": text("For agencies reselling SEO to clients under their own brand"),
    "templates.agency-white-label.goalPlaceholder": text("e.g. Close 3 new white-label retainers per month at $3,000 MRR each"),
    "templates.agency-white-label.stages.awareness": text("Traffic / Awareness"),
    "templates.agency-white-label.stages.website-visit": text("Website Visit"),
    "templates.agency-white-label.stages.lead": text("Lead / Opt-in"),
    "templates.agency-white-label.stages.discovery": text("Discovery Call"),
    "templates.agency-white-label.stages.proposal": text("Proposal Sent"),
    "templates.agency-white-label.stages.close": text("Closed / Retainer"),

    "templates.franchise-multi-location.label": text("Franchise / Multi-Location"),
    "templates.franchise-multi-location.bestFor": text("Multi-location & franchise brands"),
    "templates.franchise-multi-location.description": text("For franchise systems growing local SEO across multiple locations"),
    "templates.franchise-multi-location.goalPlaceholder": text("e.g. Drive 20 new franchise inquiries per month from organic search"),
    "templates.franchise-multi-location.stages.awareness": text("Brand Awareness"),
    "templates.franchise-multi-location.stages.local-serp": text("Local SERP Click"),
    "templates.franchise-multi-location.stages.landing": text("Landing Page Visit"),
    "templates.franchise-multi-location.stages.lead": text("Lead / Inquiry"),
    "templates.franchise-multi-location.stages.qualified": text("Qualified Lead"),
    "templates.franchise-multi-location.stages.onboarded": text("Onboarded Location"),

    "templates.ai-search-geo.label": text("AI Search / GEO"),
    "templates.ai-search-geo.bestFor": text("Brands targeting AI answer engines"),
    "templates.ai-search-geo.description": text("For brands capturing traffic from AI-generated answers and overviews"),
    "templates.ai-search-geo.goalPlaceholder": text("e.g. Increase AI citation-to-lead conversion rate to 3% within 6 months"),
    "templates.ai-search-geo.stages.ai-mention": text("AI Mention / Citation"),
    "templates.ai-search-geo.stages.click": text("Click to Site"),
    "templates.ai-search-geo.stages.engage": text("Content Engagement"),
    "templates.ai-search-geo.stages.lead": text("Lead / Opt-in"),
    "templates.ai-search-geo.stages.close": text("Closed / Customer"),

    "templates.adult-compliance.label": text("Adult / Compliance"),
    "templates.adult-compliance.bestFor": text("Adult industry with compliance needs"),
    "templates.adult-compliance.description": text("For adult industry businesses navigating compliant marketing funnels"),
    "templates.adult-compliance.goalPlaceholder": text("e.g. Generate 50 qualified leads per month through compliant channels"),
    "templates.adult-compliance.stages.awareness": text("Platform Reach"),
    "templates.adult-compliance.stages.gate": text("Age / Compliance Gate"),
    "templates.adult-compliance.stages.content": text("Content Engaged"),
    "templates.adult-compliance.stages.lead": text("Lead / Subscriber"),
    "templates.adult-compliance.stages.close": text("Paid / Customer"),

    "templates.b2b-enterprise.label": text("B2B Enterprise"),
    "templates.b2b-enterprise.bestFor": text("Enterprise & mid-market B2B teams"),
    "templates.b2b-enterprise.description": text("For enterprise teams selling SEO and GEO to large organisations"),
    "templates.b2b-enterprise.goalPlaceholder": text("e.g. Book 5 enterprise demo calls per month from organic SEO traffic"),
    "templates.b2b-enterprise.stages.awareness": text("Brand / Content Reach"),
    "templates.b2b-enterprise.stages.content": text("Content Engaged"),
    "templates.b2b-enterprise.stages.mql": text("Marketing Qualified Lead"),
    "templates.b2b-enterprise.stages.sql": text("Sales Qualified Lead"),
    "templates.b2b-enterprise.stages.demo": text("Demo / Discovery"),
    "templates.b2b-enterprise.stages.proposal": text("Proposal Sent"),
    "templates.b2b-enterprise.stages.close": text("Closed / Contract"),

    "templates.ecommerce-dtc.label": text("E-commerce / DTC"),
    "templates.ecommerce-dtc.bestFor": text("DTC & product-based e-commerce"),
    "templates.ecommerce-dtc.description": text("For direct-to-consumer brands selling physical or digital products"),
    "templates.ecommerce-dtc.goalPlaceholder": text("e.g. Increase purchase CVR to 3% from paid social and email"),
    "templates.ecommerce-dtc.stages.landing-page": text("Product Landing Page"),
    "templates.ecommerce-dtc.stages.product-detail-page": text("Product Detail Page"),
    "templates.ecommerce-dtc.stages.order-form": text("Checkout / Order"),
    "templates.ecommerce-dtc.stages.order-bump": text("Order Bump"),
    "templates.ecommerce-dtc.stages.upsell": text("Post-Purchase Upsell"),
    "templates.ecommerce-dtc.stages.thank-you-page": text("Thank You Page"),

    "templates.high-ticket-coaching.label": text("High-Ticket Coaching"),
    "templates.high-ticket-coaching.bestFor": text("Coaches & consultants with premium offers"),
    "templates.high-ticket-coaching.description": text("For coaches and consultants closing premium offers through application calls"),
    "templates.high-ticket-coaching.goalPlaceholder": text("e.g. Book 10 strategy calls per month from content marketing"),
    "templates.high-ticket-coaching.stages.vsl-page": text("VSL Page"),
    "templates.high-ticket-coaching.stages.application-page": text("Application Page"),
    "templates.high-ticket-coaching.stages.booking-page": text("Booking Page"),
    "templates.high-ticket-coaching.stages.discovery-call": text("Discovery Call"),
    "templates.high-ticket-coaching.stages.sales-page": text("Sales Presentation"),
    "templates.high-ticket-coaching.stages.order-form": text("Enrolment / Order"),
    "templates.high-ticket-coaching.stages.retainer-close": text("Client Onboarded"),

    "templates.saas-plg.label": text("SaaS / PLG"),
    "templates.saas-plg.bestFor": text("Product-led SaaS & free-to-paid models"),
    "templates.saas-plg.description": text("For product-led SaaS converting free users to paid subscribers"),
    "templates.saas-plg.goalPlaceholder": text("e.g. Reach 5% free-to-paid conversion within 90 days"),
    "templates.saas-plg.stages.landing-page": text("Website / Landing Page"),
    "templates.saas-plg.stages.pricing-page": text("Pricing Page"),
    "templates.saas-plg.stages.free-trial": text("Free Trial Sign-Up"),
    "templates.saas-plg.stages.onboarding-checklist": text("Onboarding Checklist"),
    "templates.saas-plg.stages.activation-event": text("Activation Event"),
    "templates.saas-plg.stages.in-app-upgrade": text("In-App Upgrade Prompt"),
    "templates.saas-plg.stages.order-form": text("Paid Subscription"),

    audienceLabel: text("Who is your target audience?"),
    audienceAgency: text("SEO Agency"),
    audienceFranchise: text("Franchise / Multi-Location"),
    audienceCreator: text("Content Creator"),
    audienceAdult: text("Adult Industry"),
    audienceEnterprise: text("Enterprise / B2B"),
    goalLabel: text("What's your primary goal for this funnel?"),
    goalCharCounter: text("{count}/{max} characters"),
    goalRequired: text("Please describe your goal"),

    addStageButton: text("Add Stage"),
    stageNameLabel: text("Stage name"),
    stageNamePlaceholder: text("e.g. Discovery Call"),
    stageNameAriaLabel: text("Stage {number} name"),
    stageCvrLabel: text("Conversion rate"),
    stageChangeTypeLabel: text("Change stage type"),
    stageRemoveLabel: text("Remove stage"),
    stageLimitMax: text("Maximum 8 stages reached"),
    stageLimitMin: text("Minimum 2 stages required"),
    stageVolumesLabel: text("Estimated monthly flow"),
    stageEntryAllVisitors: text("Entry stage - all visitors"),
    stageAddNoteButton: text("+ note"),
    stageHideNoteButton: text("- note"),
    stageNotePlaceholder: text("Add a note about this stage..."),
    stageInsertHere: text("Insert stage here"),
    stageInsertAfterAria: text("Insert stage after stage {number}"),

    monthlyVisitorsLabel: text("Monthly visitors (top of funnel)"),
    monthlyVisitorsHelp: text("How many people reach the first stage each month?"),
    revenuePerLeadLabel: text("Revenue per closed client (USD)"),
    revenuePerLeadHelp: text("Average deal or retainer value"),
    projectionChartTitle: text("Monthly revenue projection"),
    projectionBaselineLabel: text("Current baseline"),
    projectionMonth12Label: text("Revenue at the selected horizon"),
    projectionTotalUpliftLabel: text("Cumulative uplift over the selected horizon"),

    emailLabel: text("Work email"),
    emailPlaceholder: staticText("you@yourcompany.com"),
    emailInvalid: text("Enter a valid work email"),
    nameLabel: text("Your name (optional)"),
    namePlaceholder: staticText("Jane Smith"),
    companyLabel: text("Company (optional)"),
    companyPlaceholder: staticText("Acme Agency"),
    privacyLabel: text("I have read and accept the"),
    privacyLinkText: text("privacy notice"),
    privacyRequired: text("Please accept the privacy notice"),

    successTitle: text("Your funnel plan is ready"),
    successSubtitle: text("Your branded PDF is ready and the handoff details were saved. No email was sent automatically."),
    downloadAgainButton: text("Download Again"),
    bookCallButton: text("Book a Scoping Call"),
    relatedResourcesTitle: text("Resources to go deeper"),

    // Step 2 — Traffic channels
    channelStep2Title: text("Select traffic channels"),
    channelNoneSelected: text("Select at least one traffic channel to continue."),
    channelBreakdownTitle: text("Traffic breakdown by channel"),

    // Step 5 — Scenarios
    scenarioLabel: text("Scenario"),
    scenarioConservative: text("Conservative"),
    scenarioTarget: text("Target"),
    scenarioOptimistic: text("Optimistic"),

    draftRestoredBanner: text("We restored your previous session."),
    draftDiscardedWarning: text("Your previous draft could not be restored and was discarded."),
    draftNotSavedWarning: text("Draft could not be saved — storage may be full."),
    draftConflictTitle: text("Resume your saved funnel plan?"),
    draftConflictBody: text("You came from the Fit Quiz, but there is a saved planner draft on this device. Resume the saved plan or start from the quiz result."),
    draftConflictResumeButton: text("Resume saved plan"),
    draftConflictStartFromQuizButton: text("Start from quiz result"),
    sharePlanButton: text("Share"),
    sharePlanLinkedIn: staticText("LinkedIn"),
    sharePlanCopied: text("Link copied"),
    sharePlanFailed: text("Copy failed"),
    sharePlanAriaLabel: text("Copy an editable share link for this funnel planner step"),
    blueprintLibraryTitle: text("Blueprint presets"),
    blueprintLibraryHelp: text("Apply a reusable funnel structure with channel mix, editable planning inputs, and an optimization scenario."),
    blueprintScenarioName: text("Blueprint optimization"),
    blueprintScenarioNotes: text("Preset optimization scenario for this blueprint."),
    "blueprintCategories.call": text("Strategy call"),
    "blueprintCategories.webinar": text("Webinar"),
    "blueprintCategories.commerce": text("Commerce"),
    "blueprintCategories.saas": text("SaaS"),
    "blueprintCategories.local": text("Local"),
    "blueprintCategories.ai": text("AI search"),
    "blueprintCategories.agency": text("Agency"),
    "blueprintCategories.compliance": text("Compliance"),
    "blueprints.strategy-call-closer.label": text("Strategy call closer"),
    "blueprints.strategy-call-closer.description": text("VSL, application, booking, proposal, and close path for premium offers."),
    "blueprints.strategy-call-closer.benchmark": text("A structure for call-qualified high-ticket offers."),
    "blueprints.evergreen-webinar-engine.label": text("Evergreen webinar engine"),
    "blueprints.evergreen-webinar-engine.description": text("Registration, live or replay attendance, application, demo, and contract path."),
    "blueprints.evergreen-webinar-engine.benchmark": text("A structure for education-led mid-market or enterprise selling."),
    "blueprints.tripwire-slo-ladder.label": text("Tripwire and SLO ladder"),
    "blueprints.tripwire-slo-ladder.description": text("Opt-in, low-ticket conversion, order bump, upsell, and nurture path."),
    "blueprints.tripwire-slo-ladder.benchmark": text("A structure for a low-ticket entry offer, order bump, and upsell path."),
    "blueprints.plg-trial-activation.label": text("PLG trial activation"),
    "blueprints.plg-trial-activation.description": text("Pricing, free trial, onboarding, activation event, and in-app upgrade path."),
    "blueprints.plg-trial-activation.benchmark": text("A structure for free-to-paid SaaS activation."),
    "blueprints.multi-location-lead-engine.label": text("Multi-location lead engine"),
    "blueprints.multi-location-lead-engine.description": text("Local search, location page, quote request, call, and onboarding path."),
    "blueprints.multi-location-lead-engine.benchmark": text("A structure for franchise and local-service rollouts."),
    "blueprints.ai-search-demand-capture.label": text("AI search demand capture"),
    "blueprints.ai-search-demand-capture.description": text("AI citation, answer-click, proof, lead, call, and close path."),
    "blueprints.ai-search-demand-capture.benchmark": text("A structure for AI answer visibility and assisted-search demand."),
    "blueprints.white-label-retainer-path.label": text("White-label retainer path"),
    "blueprints.white-label-retainer-path.description": text("Content, audit tool, opt-in, discovery, proposal, and retainer close path."),
    "blueprints.white-label-retainer-path.benchmark": text("A structure for recurring agency SEO delivery."),
    "blueprints.compliance-subscription-path.label": text("Compliance subscription path"),
    "blueprints.compliance-subscription-path.description": text("Age/compliance gate, content proof, opt-in, checkout, and save path."),
    "blueprints.compliance-subscription-path.benchmark": text("A structure for sensitive subscription funnels with compliance gates."),

    errorGeneric: text("Something went wrong. Please try again."),
    errorPdfFailed: text("Could not generate your PDF. Please try again."),
    templateRequired: text("Please select a funnel type"),
    audienceRequired: text("Please select your target audience"),
    stagesMinError: text("Add at least 2 stages"),
    stagesMaxError: text("Maximum 8 stages"),
    stageNameRequired: text("Stage name is required"),
    goalCharCount: text("{count}/{max} characters"),

    // Shared wizard chrome
    progressAriaLabel: text("Funnel planner progress"),
    stepProgressLabel: text("Step {step} of {total} - about 3 minutes"),
    stepListAriaLabel: text("Steps: {steps}"),
    stepLiveRegionLabel: text("Funnel planner step {step} of {total}"),
    previousStepAriaLabel: text("Go to previous step"),
    nextStepAriaLabel: text("Go to next step"),
    startOverButton: text("Start over"),
    startOverAriaLabel: text("Start the funnel planner over from the beginning"),
    optionalLabel: text("optional"),
    requiredLabel: text("required"),
    notAvailableLabel: text("N/A"),
    perMonthLabel: text("/mo"),
    stepNameTemplate: text("Template"),
    stepNameTraffic: text("Traffic"),
    stepNameProducts: text("Products"),
    stepNameAudience: text("Audience"),
    stepNameFunnel: text("Funnel"),
    stepNameProjections: text("Projections"),
    stepNameDownload: text("Download"),

    // Error and loading states
    routeErrorMessage: text("Something went wrong loading the funnel planner."),
    errorBoundaryMessage: text("Something went wrong. Please refresh."),
    tryAgainButton: text("Try again"),

    // Audience and goal screen
    audienceAgencyDescription: text("I manage funnels for clients"),
    audienceFranchiseDescription: text("I grow a multi-location brand"),
    audienceCreatorDescription: text("I sell courses, content, or coaching"),
    audienceAdultDescription: text("I market adult-industry services"),
    audienceEnterpriseDescription: text("I run large-scale B2B campaigns"),
    audienceRecommendedBadge: text("Recommended"),
    audienceCompletedStepsAriaLabel: text("Completed steps"),
    audienceInitialPrompt: text("Choose your audience type and describe your goal to unlock your personalized funnel plan."),
    audienceLegend: text("Who is this funnel for? Select the audience type that best matches your client or project."),
    audienceLegendRequiredHint: text("(required, {count} options)"),
    goalFallbackPlaceholder: text("Grow organic B2B SaaS trial signups from 150 to 400/month by Q4 via SEO + LinkedIn"),
    goalHelper: text("Your goal shapes your funnel structure, traffic strategy, and projections."),
    audienceGoalMissing: text("Select an audience and enter your goal to continue."),
    audienceMissing: text("Select an audience to continue."),
    goalMissing: text("Enter your goal to continue."),
    readyToContinue: text("Ready to continue!"),
    audienceAdvancedPlanningTitle: text("Regional and objective assumptions"),
    audienceRegionLabel: text("Region"),
    audienceCurrencyLabel: text("Currency"),
    audienceObjectiveTypeLabel: text("Objective type"),
    audienceObjectiveNoneOption: text("Use projection default"),
    audienceObjectiveValueLabel: text("Objective value"),
    audienceTimeframeLabel: text("Timeframe months"),
    audienceBudgetCeilingLabel: text("Budget ceiling"),
    audienceRiskToleranceLabel: text("Risk tolerance"),
    audienceRiskDefaultOption: text("Not set"),
    audienceBuyingTriggerLabel: text("Buying trigger"),

    // Traffic screen
    trafficSkipToConfig: text("Skip to channel configuration"),
    trafficRemovedChannel: text("Removed {channel}"),
    trafficUndoButton: text("Undo"),
    trafficShowAllChannels: text("Show all channels"),
    trafficShowFewerChannels: text("Show fewer channels"),
    trafficExampleEmpty: text("Example: Search Organic - 5,000 visitors/mo"),
    trafficConfigRegionLabel: text("Channel configuration"),
    trafficConfigureChannelsTitle: text("Configure channels"),
    trafficSelectedCount: text("{count} selected"),
    trafficEmptyConfig: text("Click a channel on the left to set monthly volumes"),
    trafficRemoveChannelAria: text("Remove {channel}"),
    trafficVisitorsLabel: text("Visitors /mo"),
    trafficVisitorsPlaceholder: text("e.g. 5000"),
    trafficVisitorsRequired: text("Enter visitors to continue"),
    trafficAcquisitionModelLabel: text("Acquisition model"),
    trafficCostPerClickLabel: text("Cost per click (USD)"),
    trafficCostPerViewLabel: text("Cost per view (USD)"),
    trafficCpmLabel: text("CPM (USD)"),
    trafficCtrLabel: text("CTR (%)"),
    trafficFlatFeeLabel: text("Flat monthly fee (USD)"),
    trafficCostPerCustomerLabel: text("Cost per customer (USD)"),
    trafficCostPerLeadLabel: text("Cost per lead (USD)"),
    trafficRevenueShareLabel: text("Revenue share (%)"),
    trafficLaborHoursLabel: text("Hours / mo"),
    trafficHourlyCostLabel: text("Hourly cost (USD)"),
    trafficCampaignLabel: text("Campaign"),
    trafficCampaignPlaceholder: text("e.g. Q3 awareness"),
    trafficAdvancedTrackingLabel: text("Advanced tracking"),
    trafficAttributionRoleLabel: text("Attribution role"),
    trafficTrackingSetupLabel: text("Tracking setup"),
    trafficAudienceTemperatureLabel: text("Audience temperature"),
    trafficDeviceLabel: text("Device"),
    trafficGeoLabel: text("Geo / market"),
    trafficGeoPlaceholder: text("e.g. US, DACH, Tel Aviv"),
    trafficBudgetCapLabel: text("Budget cap"),
    trafficRampTypeLabel: text("Ramp type"),
    trafficConfidenceLabel: text("Confidence"),
    trafficFirstTouchOption: text("First touch"),
    trafficLastTouchOption: text("Last touch"),
    trafficAssistOption: text("Assist"),
    trafficRetargetingOption: text("Retargeting"),
    trafficDirectOption: text("Direct"),
    trafficTrackingNoneOption: text("None"),
    trafficTrackingUtmOnlyOption: text("UTM only"),
    trafficTrackingPixelOption: text("Pixel"),
    trafficTrackingServerSideOption: text("Server-side"),
    trafficTrackingCrmMatchedOption: text("CRM matched"),
    trafficTemperatureColdOption: text("Cold"),
    trafficTemperatureWarmOption: text("Warm"),
    trafficTemperatureHotOption: text("Hot"),
    trafficDeviceAllOption: text("All devices"),
    trafficDeviceDesktopOption: text("Desktop"),
    trafficDeviceMobileOption: text("Mobile"),
    trafficDeviceTabletOption: text("Tablet"),
    trafficRampFlatOption: text("Flat"),
    trafficRampLinearOption: text("Linear"),
    trafficRampSeasonalOption: text("Seasonal"),
    trafficRampLaunchSpikeOption: text("Launch spike"),
    trafficRampDecayOption: text("Decay"),
    trafficConfidenceLowOption: text("Low"),
    trafficConfidenceMediumOption: text("Medium"),
    trafficConfidenceHighOption: text("High"),

    // Product screen
    productDefaultName: text("Core Offer"),
    productLibraryTitle: text("Your Products"),
    productEmptyWarning: text("Add at least one product to unlock revenue projections."),
    productEmptyTitle: text("No products added yet"),
    productEmptyBody: text("Start with your core offer - for example: an online course, SaaS subscription, or physical product."),
    productAddFirstButton: text("Add your first product"),
    productAddButton: text("Add Product"),
    productAddLimitButton: text("Add Product ({count}/{max} - limit reached)"),
    productMaxTitle: text("Maximum {max} products reached"),
    productCountLabel: text("{count} of {max} added"),
    productMaxReached: text("Maximum of {max} products reached"),
    productTipPrefix: text("Tip:"),
    productTipBody: text("Add your core offer first - its revenue is the projection baseline and upsell take-rate is calculated against it. Add upsells or order bumps afterwards."),
    productAddTypeAria: text("Add {type} product"),
    productMoveUpTitle: text("Move up"),
    productMoveDownTitle: text("Move down"),
    productMoveUpAria: text("Move {product} up"),
    productMoveDownAria: text("Move {product} down"),
    productNameLabel: text("Product {index} Name"),
    productNamePlaceholder: text("e.g. Core Offer, Order Bump, Upsell 1"),
    productRemoveAria: text("Remove product"),
    productTypeLabel: text("Product Type"),
    productPriceTypeLabel: text("Price Type"),
    productPriceLabel: text("Price"),
    productPriceWithSuffixLabel: text("Price ({suffix})"),
    productRecurringPricing: text("Recurring Pricing"),
    productMonthlyChurnLabel: text("Monthly Churn %"),
    productMonthsRetainedLabel: text("Avg Months Retained"),
    productFreeTrialLabel: text("Offer a free trial"),
    productTrialDaysLabel: text("Trial Days"),
    productTrialToPaidLabel: text("Trial-to-Paid CVR %"),
    productAdvancedLabel: text("Advanced"),
    productRefundRateLabel: text("Refund Rate %"),
    productCogsLabel: text("Production Costs %"),
    productMerchantFeeLabel: text("Merchant Fee %"),
    productGuaranteeLabel: text("Guarantee"),
    productUpsellLabel: text("This is an upsell / order bump"),
    productUpsellTakeRateLabel: text("Upsell Take Rate %"),
    productOfferRoleLabel: text("Offer role"),
    productCheckoutTypeLabel: text("Checkout type"),
    productAttachedStageLabel: text("Attached stage"),
    productNoAttachedStageOption: text("No specific stage"),
    productSalesSplitLabel: text("Sales split %"),
    productOfferCoreOption: text("Core offer"),
    productOfferOrderBumpOption: text("Order bump"),
    productOfferUpsellOption: text("Upsell"),
    productOfferDownsellOption: text("Downsell"),
    productOfferBackendOption: text("Backend offer"),
    productOfferContinuityOption: text("Continuity"),
    productOfferApplicationOnlyOption: text("Application only"),
    productOfferQuoteOnlyOption: text("Quote only"),
    productCheckoutOneStepOption: text("One-step checkout"),
    productCheckoutTwoStepOption: text("Two-step checkout"),
    productCheckoutInvoiceOption: text("Invoice"),
    productCheckoutSubscriptionOption: text("Subscription checkout"),
    productCheckoutSalesCallOption: text("Sales call close"),
    productOrderBumpTakeRateLabel: text("Order bump take rate %"),
    productDownsellTakeRateLabel: text("Downsell take rate %"),
    productMerchantFixedFeeLabel: text("Processor fixed fee"),
    productFulfillmentCostLabel: text("Fulfillment cost"),
    productFulfillmentCostTypeLabel: text("Fulfillment type"),
    productFulfillmentNoneOption: text("None"),
    productFulfillmentOneTimeOption: text("One time"),
    productFulfillmentRecurringOption: text("Recurring"),
    productFulfillmentPerOrderOption: text("Per order"),
    validationRequired: text("Required"),
    validationEnterNumber: text("Enter a number"),
    validationMinimum: text("Minimum is {min}"),
    validationMaximum: text("Maximum is {max}"),

    // Canvas and flow screens
    canvasToolbarTitle: text("Funnel Canvas"),
    canvasAddSourceAria: text("Add traffic source"),
    canvasSourceButton: text("Source"),
    canvasChooseChannelLabel: text("Choose traffic channel"),
    canvasAddStageAria: text("Add funnel stage"),
    canvasStageButton: text("Stage"),
    canvasChooseStageTypeLabel: text("Choose stage type"),
    canvasHint: text("Drag handles to connect - Drag nodes to reposition - Scroll to zoom - Right-click any node to edit"),
    canvasNoStageAlert: text("Add at least one stage to continue"),
    canvasNewSourceLabel: text("New Source"),
    canvasPlannerMenuTitle: text("Funnel Planner"),
    canvasStageSearchPlaceholder: text("Search stages"),
    canvasStageSearchAria: text("Search funnel stage types"),
    canvasRecommendedStageTypesLabel: text("Recommended"),
    canvasAddStageHere: text("Add funnel stage here"),
    canvasAddSourceHere: text("Add traffic source here"),
    canvasAddBranchHere: text("Add branch split here"),
    canvasAddRetargetingHere: text("Add retargeting step"),
    canvasAddWaitHere: text("Add wait/delay step"),
    canvasAddSurveyHere: text("Add survey/quiz split"),
    canvasEdgeMenuTitle: text("Funnel connector"),
    canvasEdgeTypeField: text("Connector type"),
    canvasEdgeSplitField: text("Split (%)"),
    canvasEdgeCvrField: text("Edge CVR (%)"),
    canvasEdgeOptionalField: text("Optional path"),
    canvasDeleteEdgeButton: text("Delete connector"),
    canvasNodeKindField: text("Node role"),
    canvasUrlField: text("URL / asset"),
    canvasPublishedStatusField: text("Status"),
    canvasTrackingStatusField: text("Tracking"),
    canvasWaitDaysField: text("Wait days"),
    canvasDueInDaysField: text("Due in days"),
    canvasActualVolumeField: text("Actual volume"),
    canvasActualCvrField: text("Actual CVR (%)"),
    canvasOwnerField: text("Owner"),
    canvasBoxColorField: text("Box color"),
    canvasCustomColorField: text("Custom color"),
    canvasResetColorButton: text("Reset"),
    canvasBoxWidthField: text("Width"),
    canvasBoxHeightField: text("Height"),
    canvasResizeHelp: text("Select a box and drag its edge or corner handles to resize it."),
    canvasColorSwatchAria: text("Set box color {color}"),
    canvasOptionalStageField: text("Optional stage"),
    canvasCopyStageLinkButton: text("Copy stage link"),
    canvasStageLinkCopied: text("Stage link copied"),
    canvasFitViewButton: text("Fit view"),
    canvasClearSelectionButton: text("Clear selection"),
    canvasTrafficSourceTitle: text("Traffic Source"),
    canvasFunnelStageTitle: text("Funnel Stage"),
    canvasLabelField: text("Label"),
    canvasNameField: text("Name"),
    canvasVisitorsField: text("Visitors / mo"),
    canvasChannelTypeField: text("Channel type"),
    canvasStageTypeField: text("Stage type"),
    canvasConversionRateField: text("Conversion rate (%)"),
    canvasDeleteNodeButton: text("Delete node"),
    canvasStageRemoveTitle: text("Remove stage"),
    canvasStageRemoveAria: text("Remove this stage"),
    canvasStageTypeChangeTitle: text("Stage type - click to change"),
    canvasClickToEditName: text("Click to edit name"),
    canvasClickToName: text("Click to name..."),
    canvasCvrTitle: text("Conversion Rate - percent of visitors who proceed to the next stage"),
    canvasVolumeTitle: text("{count} visitors reach this stage per month"),
    canvasPerMonthTitle: text("Per month"),
    canvasChannelTitle: text("Channel: {channel}"),
    canvasClickToEditVisitors: text("Click to edit visitors/mo"),
    canvasMonthlyVisitorsTitle: text("Monthly visitors from this channel"),
    canvasShowKpiOverlayButton: text("Show KPIs"),
    canvasHideKpiOverlayButton: text("Hide KPIs"),
    canvasKpiOverlayTitle: text("Canvas KPI overlay"),
    canvasOverlayVisitors: text("Visitors"),
    canvasOverlayFinalStage: text("Final stage"),
    canvasOverlayBlendedCvr: text("Blended CVR"),
    canvasOverlayStages: text("Stages"),
    canvasOverlayWaitDays: text("Wait days"),
    flowAriaLabel: text("Funnel flow: {stages}"),
    flowPreviewLabel: text("Funnel preview"),
    flowUnnamedStage: text("Unnamed"),
    flowCustomStage: text("Custom"),

    // Projection screen
    projectionFunnelLabel: text("Your funnel"),
    projectionScenarioTitle: text("Revenue Scenario"),
    projectionHorizonLabel: text("Projection horizon (months, 1-36)"),
    projectionHorizonRevenueColumn: text("Monthly revenue at month {month}"),
    projectionHorizonDeltaColumn: text("Change at month {month}"),
    projectionFallbackMonthlyVisitorsLabel: text("Template monthly visitors preset"),
    projectionFallbackRevenuePerLeadLabel: text("Revenue per final-stage conversion"),
    projectionRevenuePerFinalConversionHelp: text("The projection values each final-stage conversion at this amount. Product-level revenue and P&L are reported separately."),
    projectionScenarioAria: text("Revenue scenario: {scenario}"),
    projectionScenarioConservative: text("Conservative (x0.8)"),
    projectionScenarioTarget: text("Target"),
    projectionScenarioOptimistic: text("Optimistic (x1.2)"),
    projectionMonth12Revenue: text("Monthly revenue at month {month}"),
    projectionScenarioComparisonTitle: text("Scenario comparison"),
    projectionScenarioComparisonCaption: text("Named scenario comparison by monthly revenue at month {month}"),
    projectionExportJsonButton: text("Export JSON"),
    projectionExportCsvButton: text("Export CSV"),
    projectionScenarioColumn: text("Scenario"),
    projectionMonth12Column: text("Month {month}"),
    projectionDeltaColumn: text("Delta"),
    projectionChangedAssumptionsColumn: text("Changed assumptions"),
    projectionReverseGoalTitle: text("Reverse goal planner"),
    projectionReverseGoalType: text("Goal type"),
    projectionReverseGoalTarget: text("Target"),
    projectionReverseGoalResult: text("Required visitors"),
    projectionReverseGoalGap: text("Visitor gap"),
    projectionReverseGoalBudget: text("Budget"),
    projectionGoalProfit: text("Profit"),
    projectionGoalRevenue: text("Revenue"),
    projectionGoalLeads: text("Leads"),
    projectionGoalCustomers: text("Customers"),
    projectionGoalBookedCalls: text("Booked calls"),
    projectionGoalCac: text("CAC"),
    projectionGoalPayback: text("Payback months"),
    projectionReverseGoalCurrentValue: text("Current value"),
    projectionReverseGoalTargetGap: text("Target gap"),
    projectionScenarioBuilderTitle: text("Custom scenario builder"),
    projectionScenarioBuilderHelp: text("Create editable snapshots for stress cases, launch plans, or upside assumptions."),
    projectionAddScenarioButton: text("Add scenario"),
    projectionScenarioNameField: text("Scenario name"),
    projectionScenarioCvrMultiplierField: text("CVR multiplier"),
    projectionScenarioTrafficMultiplierField: text("Traffic multiplier"),
    projectionScenarioRevenueMultiplierField: text("Revenue multiplier"),
    projectionScenarioNotesField: text("Notes"),
    projectionScenarioNotesPlaceholder: text("What changed?"),
    projectionCustomScenarioDefaultName: text("Custom scenario"),
    projectionRemoveScenarioButton: text("Remove"),
    projectionScenarioAssumptionCvr: text("CVR x{value}"),
    projectionScenarioAssumptionTraffic: text("Traffic x{value}"),
    projectionScenarioAssumptionRevenue: text("Revenue yield x{value}"),
    projectionScenarioAssumptionCustom: text("Custom assumptions"),
    projectionScenarioDefaultBaselineName: text("Baseline"),
    projectionScenarioDefaultConservativeName: text("Conservative"),
    projectionScenarioDefaultStressName: text("Stress"),
    projectionScenarioDefaultOptimisticName: text("Optimistic"),
    projectionScenarioAssumptionCurrentInputs: text("Current planner inputs"),
    projectionScenarioAssumptionLowerConversion: text("Lower conversion"),
    projectionScenarioAssumptionLowerTraffic: text("Lower traffic"),
    projectionScenarioAssumptionLowerRevenueYield: text("Lower revenue yield"),
    projectionScenarioAssumptionMajorCvrDrop: text("Major CVR drop"),
    projectionScenarioAssumptionTrafficUnderDelivery: text("Traffic under-delivery"),
    projectionScenarioAssumptionHigherConversion: text("Higher conversion"),
    projectionScenarioAssumptionHigherTraffic: text("Higher traffic"),
    projectionScenarioAssumptionHigherRevenueYield: text("Higher revenue yield"),
    projectionCommandCenterTitle: text("Projection command center"),
    projectionCommandCenterHelp: text("Use the tabs as a review path: projection math first, execution readiness second, actuals third, and final reports last. Hover or focus the help markers on fields when an assumption is unclear."),
    projectionCommandCenterRevenueHelp: text("Projected monthly revenue at month {month} from the active graph or linear fallback."),
    projectionCommandCenterVisitorsHelp: text("Total modeled monthly visitors from selected channels or graph source nodes."),
    projectionCommandCenterCustomersHelp: text("Estimated monthly customers after graph splits, edge CVR, optional paths, waits, loops, and stage CVR."),
    projectionCommandCenterExpensesHelp: text("Computed monthly operating cost from one-time, monthly, quarterly, annual, and timed expense rows."),
    projectionReportWorkspaceTitle: text("Report workspace"),
    projectionReportWorkspaceHelp: text("Switch between projection, execution, actuals, and report outputs without losing the detailed tables below."),
    projectionReportTabsLabel: text("Planner report tabs"),
    projectionTabProjection: text("Projection"),
    projectionTabExecution: text("Execution"),
    projectionTabActuals: text("Actuals"),
    projectionTabReports: text("Reports"),
    projectionTabProjectionHelp: text("Model traffic, conversion, revenue, P&L, and sensitivity from the current funnel graph."),
    projectionTabExecutionHelp: text("Review expenses, asset coverage, published status, and delivery readiness before handoff."),
    projectionTabActualsHelp: text("Compare measured stage volume and CVR against the normalized forecast period."),
    projectionTabReportsHelp: text("Check model health and client handoff outputs before exporting the final plan."),
    projectionPublishedStatusReportTitle: text("Published-status report"),
    projectionActualAttentionTitle: text("Needs review"),
    projectionClientHandoffChecklistTitle: text("Client handoff checklist"),
    projectionClientHandoffChecklistHelp: text("Confirm model health, asset ownership, tracking status, and exported assumptions before handoff."),
    projectionRulesTitle: text("Rules-based recommendations"),
    projectionRecommendationSourceBenchmarkDefault: text("Planner reference"),
    projectionRecommendationSourceUserInput: text("User input"),
    projectionRecommendationSourceCalculated: text("Calculated"),
    projectionRecommendationSourceEstimated: text("Estimated"),
    projectionRecommendationBudgetLow: text("low-budget"),
    projectionRecommendationBudgetBaseline: text("baseline"),
    projectionRecommendationBudgetScaling: text("scaling-budget"),
    projectionRecommendationTrackingMissingTitle: text("Tracking is incomplete"),
    projectionRecommendationTrackingMissingDetail: text("At least one traffic channel has no tracking setup, so source-level ROI will be hard to validate."),
    projectionRecommendationTrackingMissingAction: text("Add UTM or pixel/server-side tracking before scaling spend."),
    projectionRecommendationChannelTitle: text("Add {channel} to round out the channel mix"),
    projectionRecommendationChannelDetail: text("Recommended for the {template} template with regional defaults and {budget} planning."),
    projectionRecommendationChannelAction: text("Add this channel in step 2 or use it as the next traffic test before increasing spend."),
    projectionRecommendationNoCustomersTitle: text("The funnel does not produce customers"),
    projectionRecommendationNoCustomersDetail: text("Current traffic and conversion assumptions round down to zero monthly customers."),
    projectionRecommendationNoCustomersAction: text("Increase top-of-funnel volume, simplify stages, or improve the lowest-CVR stage."),
    projectionRecommendationStageCvrTitle: text("A stage CVR looks too aggressive"),
    projectionRecommendationStageCvrDetail: text("{stage} is modeled at {cvr}% conversion."),
    projectionRecommendationStageCvrAction: text("Stress-test this assumption with a lower CVR before using the forecast for budget decisions."),
    projectionRecommendationTrafficConfidenceTitle: text("Traffic scale needs stronger confidence"),
    projectionRecommendationTrafficConfidenceDetail: text("The plan models high monthly traffic without any high-confidence channel source."),
    projectionRecommendationTrafficConfidenceAction: text("Mark the channel confidence honestly or split the model into ramp phases."),
    projectionRecommendationLeverageTitle: text("{stage} is the strongest bottleneck"),
    projectionRecommendationLeverageDetail: text("It is below the planner reference rate and has the highest +10% conversion lift impact."),
    projectionRecommendationLeverageAction: text("Prioritize copy, offer, page speed, proof, or sales-process tests on this stage first."),
    projectionRecommendationMarginLowTitle: text("Margin is too thin"),
    projectionRecommendationMarginLowDetail: text("Modeled net margin is {margin}%, before any unmodeled overhead."),
    projectionRecommendationMarginLowAction: text("Review price, COGS, refund rate, traffic cost, or add a higher-margin upsell."),
    projectionRecommendationCacMarginTitle: text("CAC is higher than unit margin"),
    projectionRecommendationCacMarginDetail: text("Modeled CAC is {cac}, above the primary offer margin."),
    projectionRecommendationCacMarginAction: text("Lower acquisition cost, increase price, improve CVR, or add a higher-margin backend offer."),
    projectionRecommendationPaybackLongTitle: text("CAC payback is too long"),
    projectionRecommendationPaybackLongDetail: text("Modeled CAC payback is {months} months."),
    projectionRecommendationPaybackLongAction: text("Tighten paid spend, improve activation, raise expansion revenue, or use a lower-risk traffic ramp."),
    projectionRecommendationRoasLowTitle: text("ROAS is below a healthy scaling range"),
    projectionRecommendationRoasLowDetail: text("Current modeled ROAS is {roas}x."),
    projectionRecommendationRoasLowAction: text("Hold paid scaling until conversion rate, AOV, or traffic cost improves."),
    projectionRecommendationCapacityTitle: text("Sales capacity caps the upside"),
    projectionRecommendationCapacityDetail: text("The modeled demand exceeds the configured sales-call capacity."),
    projectionRecommendationCapacityAction: text("Increase call capacity, tighten qualification, or push low-intent leads into nurture."),
    projectionRecommendationOrderBumpTitle: text("Add an order bump test"),
    projectionRecommendationOrderBumpDetail: text("Commerce-style funnels usually need a low-friction basket lift before scaling paid traffic."),
    projectionRecommendationOrderBumpAction: text("Add a small order bump in step 3 and attach it to the checkout or order-form stage."),
    projectionRecommendationUpsellTitle: text("Add a backend or upsell path"),
    projectionRecommendationUpsellDetail: text("The current offer stack has no modeled upsell or backend revenue lever."),
    projectionRecommendationUpsellAction: text("Add an upsell, downsell, or backend offer in step 3 before finalizing the projection."),
    projectionRecommendationFirstExperimentTitle: text("Create the first controlled experiment"),
    projectionRecommendationFirstExperimentDetail: text("{stage} is the best first place to define a test."),
    projectionRecommendationFirstExperimentFallbackDetail: text("No experiment is attached to the funnel yet."),
    projectionRecommendationFirstExperimentAction: text("Add an A/B test in the experiment plan and choose one KPI to judge the winner."),
    projectionRecommendationHealthyTitle: text("No critical modeling gap detected"),
    projectionRecommendationHealthyDetail: text("The current assumptions are internally consistent for planning."),
    projectionRecommendationHealthyAction: text("Use the delivery roadmap and experiment plan to turn the model into an execution sequence."),
    projectionModelHealthTitle: text("Model health"),
    projectionModelHealthHelp: text("Structural checks flag graph paths that can distort the simulator before client handoff."),
    projectionModelHealthClear: text("No structural warnings are active."),
    projectionModelHealthWarningCount: text("{count} warnings need review."),
    projectionOperatingModelTitle: text("Operating model"),
    projectionRegionCurrency: text("Region / currency"),
    projectionTimeDelay: text("Modeled delay"),
    projectionDaysValue: text("{count} days"),
    projectionCapacityStatus: text("Capacity"),
    projectionReinvestmentTitle: text("Traffic reinvestment"),
    projectionReinvestmentHelp: text("Model how much baseline projected monthly revenue is recycled into additional traffic each month."),
    projectionModeledReinvestment: text("Modeled reinvestment"),
    projectionReinvestmentPercentField: text("Reinvest %"),
    projectionReinvestmentCapField: text("Monthly cap"),
    projectionReinvestmentEffectField: text("Effect at month {month}"),
    projectionCapacityPlannerTitle: text("Capacity planner"),
    projectionCapacityPlannerHelp: text("Cap sales-call throughput so the projection flags capacity bottlenecks before launch."),
    projectionEnableCapacityButton: text("Enable capacity cap"),
    projectionDisableCapacityButton: text("Disable capacity cap"),
    projectionMaxCallsField: text("Max calls / mo"),
    projectionSalesRepsField: text("Sales reps"),
    projectionRepHoursField: text("Hours / rep"),
    projectionCallDurationField: text("Call mins"),
    projectionRepCloseRateField: text("Rep close %"),
    projectionExpenseLibraryTitle: text("Expense library"),
    projectionExpenseLibraryHelp: text("Track monthly, one-time, quarterly, and annual execution costs for the P&L and reverse-goal model."),
    projectionExpenseLibraryCaption: text("Expense library by category, amount, frequency, timing, and attachment"),
    projectionExpenseLabelColumn: text("Expense"),
    projectionExpenseCategoryColumn: text("Category"),
    projectionExpenseAmountColumn: text("Amount"),
    projectionExpenseFrequencyColumn: text("Frequency"),
    projectionExpenseMonthlyColumn: text("Monthly total"),
    projectionExpenseStartMonthColumn: text("Start"),
    projectionExpenseEndMonthColumn: text("End"),
    projectionExpenseEmpty: text("No operating expenses modeled yet. Add a preset above or keep expenses at zero."),
    projectionExpenseLabelAria: text("Expense label"),
    projectionExpenseGeneralCategory: text("General"),
    projectionExpenseFrequencyOneTime: text("One-time"),
    projectionExpenseFrequencyMonthly: text("Monthly"),
    projectionExpenseFrequencyQuarterly: text("Quarterly"),
    projectionExpenseFrequencyAnnual: text("Annual"),
    projectionRemoveExpenseButton: text("Remove"),
    projectionActualPlanTitle: text("Plan vs actual"),
    projectionActualPlanHelp: text("Enter real stage volume and CVR to compare execution against the forecast."),
    projectionActualPlanCaption: text("Planned and actual stage conversion and volume comparison"),
    projectionPlannedCvrColumn: text("Planned CVR"),
    projectionActualCvrColumn: text("Actual CVR"),
    projectionPlannedVolumeColumn: text("Planned volume"),
    projectionActualVolumeColumn: text("Actual volume"),
    projectionActualNormalizedVolumeColumn: text("Normalized monthly actual"),
    projectionActualPeriodDaysColumn: text("Actual period days"),
    projectionActualCvrDeltaColumn: text("CVR drift"),
    projectionActualVolumeDeltaColumn: text("Volume drift"),
    projectionStatusColumn: text("Status"),
    projectionActualPlanHealthy: text("No material drift detected in stages with actuals."),
    projectionActualPlanMissing: text("{count} stages still need actuals before drift can be judged."),
    projectionActualPlanBehind: text("{count} stages are behind plan and need review."),
    projectionActualPlanAttention: text("{count} actual rows need period review."),
    projectionActualStatusMissing: text("Missing actuals"),
    projectionActualStatusPartial: text("Partial period"),
    projectionActualStatusStale: text("Stale"),
    projectionActualStatusAhead: text("Ahead"),
    projectionActualStatusOnTrack: text("On track"),
    projectionActualStatusBehind: text("Behind"),
    projectionActualVolumeAria: text("Actual volume for {stage}"),
    projectionActualCvrAria: text("Actual CVR for {stage}"),
    projectionActualPeriodDaysAria: text("Actual period days for {stage}"),
    projectionTrackingPlanTitle: text("Per-node tracking plan"),
    projectionTrackingPlanHelp: text("Use these generated event names and fields to instrument each funnel node in GTM and PostHog."),
    projectionTrackingPlanCaption: text("Generated tracking plan by stage, event name, GTM fields, PostHog fields, conversion definition, attribution role, and tracking status"),
    projectionTrackingEventColumn: text("Event name"),
    projectionTrackingTriggerColumn: text("Trigger"),
    projectionTrackingGtmFieldsColumn: text("GTM fields"),
    projectionTrackingPosthogFieldsColumn: text("PostHog fields"),
    projectionTrackingConversionColumn: text("Conversion definition"),
    projectionTrackingAttributionColumn: text("Attribution"),
    projectionTrackingStatusColumn: text("Tracking"),
    projectionTrackingStatusNotSet: text("Not set"),
    projectionTrackingStatusNone: text("No tracking"),
    projectionTrackingStatusPlanned: text("Planned"),
    projectionTrackingStatusUtmOnly: text("UTM only"),
    projectionTrackingStatusPixel: text("Pixel"),
    projectionTrackingStatusInstalled: text("Installed"),
    projectionTrackingStatusServerSide: text("Server-side"),
    projectionTrackingStatusCrmMatched: text("CRM matched"),
    projectionTrackingStatusVerified: text("Verified"),
    projectionTrackingStatusBroken: text("Broken"),
    projectionTrackingTriggerPageView: text("Page view"),
    projectionTrackingTriggerFormSubmit: text("Form submit"),
    projectionTrackingTriggerEmailClick: text("Email click"),
    projectionTrackingTriggerBookingSubmit: text("Booking or application submit"),
    projectionTrackingTriggerPurchase: text("Purchase or checkout"),
    projectionTrackingTriggerActivation: text("Activation event"),
    projectionTrackingTriggerCustomEvent: text("Custom event"),
    projectionTrackingConversionDefinition: text("Count this node when it produces {metric}."),
    projectionTrackingAttributionFirstTouch: text("First-touch source"),
    projectionTrackingAttributionAssist: text("Assist / nurture touch"),
    projectionTrackingAttributionConversion: text("Conversion touch"),
    projectionTrackingAttributionRetention: text("Retention touch"),
    projectionMetricVisitors: text("visitors"),
    projectionMetricLeads: text("leads"),
    projectionMetricQualifiedLeads: text("qualified leads"),
    projectionMetricBookedCalls: text("booked calls"),
    projectionMetricAttendees: text("attendees"),
    projectionMetricTrials: text("trials"),
    projectionMetricCustomers: text("customers"),
    projectionMetricSubscribers: text("subscribers"),
    projectionMetricRevenue: text("revenue"),
    projectionDeliveryTitle: text("Client delivery roadmap"),
    projectionDeliveryCaption: text("Execution checklist by stage owner, deadline, asset status, and priority"),
    projectionDeliveryStageColumn: text("Stage"),
    projectionDeliveryOwnerColumn: text("Owner"),
    projectionDeliveryDueColumn: text("Due"),
    projectionDeliveryStatusColumn: text("Asset status"),
    projectionDeliveryPriorityColumn: text("Priority"),
    projectionDeliveryUnassigned: text("Unassigned"),
    projectionAssetInventoryTitle: text("Asset inventory"),
    projectionAssetInventoryHelp: text("Track the execution asset for each stage, including source system, owner, status, tracking, and CTA notes."),
    projectionAssetInventoryCaption: text("Asset inventory by stage, URL, source, type, owner, status, tracking, and notes"),
    projectionAssetUrlColumn: text("Asset URL"),
    projectionAssetTypeColumn: text("Asset type"),
    projectionAssetSourceColumn: text("Source system"),
    projectionAssetNotesColumn: text("CTA / output notes"),
    projectionAssetUrlAria: text("Asset URL for {stage}"),
    projectionAssetNotesAria: text("CTA or output notes for {stage}"),
    projectionExperimentsTitle: text("Experiment plan"),
    projectionExperimentBuilderTitle: text("A/B test builder"),
    projectionExperimentBuilderHelp: text("Attach tests to funnel stages and track split, KPI, winner, and actual uplift."),
    projectionAddExperimentButton: text("Add experiment"),
    projectionExperimentStageField: text("Stage"),
    projectionExperimentHypothesisField: text("Hypothesis"),
    projectionExperimentHypothesisPlaceholder: text("Changing the proof block will lift opt-ins."),
    projectionExperimentPrimaryKpiField: text("Primary KPI"),
    projectionExperimentSplitField: text("Traffic split %"),
    projectionExperimentVariantAField: text("Variant A"),
    projectionExperimentVariantBField: text("Variant B"),
    projectionExperimentStatusField: text("Status"),
    projectionExperimentStatusIdea: text("Idea"),
    projectionExperimentStatusRunning: text("Running"),
    projectionExperimentStatusPaused: text("Paused"),
    projectionExperimentStatusWinner: text("Winner"),
    projectionExperimentStatusLost: text("Lost"),
    projectionExperimentUpliftField: text("Actual uplift %"),
    projectionExperimentsCaption: text("Experiment plan by stage, KPI, status, winner, and actual uplift"),
    projectionExperimentWinnerField: text("Winner"),
    projectionExperimentWinnerNone: text("None"),
    projectionExperimentsEmpty: text("No experiments planned yet. Add one above."),
    projectionActionsColumn: text("Actions"),
    projectionRemoveExperimentButton: text("Remove"),
    projectionHelpScenarioToggle: text("Switches the displayed projection between conservative, target, and optimistic assumptions without changing the saved model."),
    projectionHelpScenarioName: text("Name the custom case so it is clear in scenario reports and exports."),
    projectionHelpScenarioCvrMultiplier: text("Multiplies every modeled conversion rate in this scenario to test downside or upside funnel efficiency."),
    projectionHelpScenarioTrafficMultiplier: text("Multiplies modeled visitors to test traffic under-delivery, launch spikes, or growth plans."),
    projectionHelpScenarioRevenueMultiplier: text("Multiplies revenue yield to model pricing, AOV, margin, or offer-mix changes."),
    projectionHelpScenarioNotes: text("Short internal explanation of what changed in this scenario for handoff context."),
    projectionHelpReverseGoalType: text("Choose the target metric the simulator should work backward from."),
    projectionHelpReverseGoalTarget: text("The desired value for the selected goal, such as revenue, leads, customers, CAC, or payback months."),
    projectionHelpReinvestmentPercent: text("Percent of baseline projected monthly revenue recycled into additional traffic each month."),
    projectionHelpReinvestmentCap: text("Monthly reinvestment ceiling. Leave blank for no cap; setting it to zero stops reinvestment."),
    projectionHelpReinvestmentEffect: text("The target projection after reinvestment assumptions are applied to the traffic model."),
    projectionHelpMaxCalls: text("Optional hard cap for sales or discovery calls the team can take in a month."),
    projectionHelpSalesReps: text("Number of people available to handle qualified sales calls."),
    projectionHelpRepHours: text("Monthly selling hours per rep after admin time and non-call work."),
    projectionHelpCallDuration: text("Average length of one sales, discovery, or application call in minutes."),
    projectionHelpRepCloseRate: text("Expected close rate after a qualified call reaches a rep."),
    projectionHelpExpenseLabel: text("Internal name for the cost item shown in exports, PDF, and CRM handoff."),
    projectionHelpExpenseCategory: text("Groups the cost into SEO/content, development, tracking, creative, compliance, sales ops, tools, labor, or general overhead."),
    projectionHelpExpenseAmount: text("Cost amount before frequency normalization. The simulator converts it into a monthly total."),
    projectionHelpExpenseFrequency: text("Controls whether the amount is counted once, monthly, quarterly, or annually."),
    projectionHelpExpenseStartMonth: text("First projection month where this cost should be included."),
    projectionHelpExpenseEndMonth: text("Optional last projection month for temporary launch or contractor costs."),
    projectionHelpActualVolume: text("Measured stage volume from the selected actuals period before monthly normalization."),
    projectionHelpActualNormalizedVolume: text("Actual volume scaled to a 30-day period so partial periods compare fairly with the forecast."),
    projectionHelpActualPeriodDays: text("Number of days covered by the actuals you entered for this stage."),
    projectionHelpActualCvr: text("Measured conversion rate for this stage during the actuals period."),
    projectionHelpActualDrift: text("Difference between forecast and normalized actuals after period adjustment."),
    projectionHelpTrackingStatus: text("Shows how instrumented the stage is, from no tracking through verified CRM-matched measurement."),
    projectionHelpAssetUrl: text("Link to the live, draft, or source asset used for this stage. It is stored in CRM and exports, not sent to PostHog."),
    projectionHelpAssetType: text("Classifies the deliverable, such as page, video, email, ad, form, checkout, or report."),
    projectionHelpAssetSource: text("Where the asset is managed or measured, such as CMS, CRM, GTM, PostHog, client site, or SEOH."),
    projectionHelpAssetOwner: text("Person or team responsible for building, publishing, or maintaining this stage asset."),
    projectionHelpAssetStatus: text("Delivery state for the asset, from unassigned through measured or needs fix."),
    projectionHelpAssetNotes: text("Notes for interactive-video CTA behavior, output expectations, or handoff details."),
    projectionHelpExperimentStage: text("Stage where the experiment runs and where uplift should be measured."),
    projectionHelpExperimentHypothesis: text("Plain-language statement of what change should improve the selected KPI."),
    projectionHelpExperimentPrimaryKpi: text("Main metric used to decide whether the experiment wins."),
    projectionHelpExperimentSplit: text("Percent of traffic assigned to the test variant for planning and handoff."),
    projectionHelpExperimentVariantA: text("Control version name or short description."),
    projectionHelpExperimentVariantB: text("Test version name or short description."),
    projectionHelpExperimentStatus: text("Current test state for execution reporting."),
    projectionHelpExperimentWinner: text("Winning variant once the experiment has a decision, or none while undecided."),
    projectionHelpExperimentUplift: text("Measured lift after the test has enough data; blank means not measured yet."),
    projectionProductRevenueTitle: text("Product-level revenue"),
    projectionProductRevenueCaption: text("Product revenue and margin by offer role"),
    projectionProductColumn: text("Product"),
    projectionRoleColumn: text("Role"),
    projectionUnitsColumn: text("Units"),
    projectionNetRevenueColumn: text("Net revenue"),
    projectionMarginColumn: text("Margin"),
    projectionPnlTitle: text("Revenue & P&L Breakdown"),
    projectionPnlLocked: text("Add products in step 3 to unlock P&L breakdown."),
    projectionPnlCaption: text("Revenue and profit and loss breakdown"),
    projectionGrossRevenue: text("Gross Revenue"),
    projectionRefunds: text("Refunds"),
    projectionMerchantFees: text("Merchant Fees"),
    projectionCogs: text("COGS"),
    projectionNetRevenue: text("Net Revenue"),
    projectionTrafficCost: text("Traffic Cost"),
    projectionGrossProfit: text("Gross Profit"),
    projectionNetProfit: text("Net Profit"),
    projectionProfitMargin: text("Profit Margin"),
    projectionOperatingExpenses: text("Operating Expenses"),
    projectionMonthlyUsd: text("monthly USD"),
    projectionOpexAria: text("Monthly operating expenses in USD"),
    projectionPositiveMetricAria: text("{metric}: positive {value}"),
    projectionNegativeMetricAria: text("{metric}: negative {value}"),
    projectionKpiTitle: text("Key Performance Indicators"),
    projectionRequiresTrafficCostTitle: text("Requires traffic cost data"),
    projectionRoasTooltip: text("Add traffic costs in step 2 to calculate ROAS"),
    projectionKpiTargetRoas: text("Target: 3x+"),
    projectionCostPerAcquisition: text("Cost per acquisition"),
    projectionLifetimeValue: text("Lifetime value"),
    projectionCacPayback: text("CAC Payback"),
    projectionCacPaybackHelp: text("Months to recover CAC"),
    projectionBreakEvenVisitors: text("Break-Even Visitors"),
    projectionBreakEvenHelp: text("Visitors to $0 profit"),
    projectionContributionMargin: text("Contribution Margin"),
    projectionKpiTargetMargin: text("Target: 50%+"),
    projectionTrafficBreakdownTitle: text("Traffic Channel Breakdown"),
    projectionTrafficBreakdownCaption: text("Traffic channel breakdown by visitors, cost, and ROAS"),
    projectionChannelColumn: text("Channel"),
    projectionVisitorsColumn: text("Visitors / mo"),
    projectionEstimatedCostColumn: text("Est. Cost"),
    projectionCplColumn: text("CPL"),
    projectionRoasColumn: text("ROAS"),
    projectionTotalLabel: text("Total"),
    projectionLeverageTitle: text("Funnel Leverage Analysis"),
    projectionLeverageHelp: text("Which stages have the biggest impact on revenue?"),
    projectionLeverageLocked: text("Add products to see leverage analysis."),
    projectionLeverageCaption: text("Funnel leverage analysis by stage conversion rate and revenue impact"),
    projectionFallbackPresetNote: text("Any visitor or per-final-stage-conversion fallback comes from the selected template or starting planner inputs. It is editable and is not observed industry evidence."),
    projectionReinvestmentPresetNote: text("The selected template supplies starting reinvestment settings; edit the rate and cap to match this plan."),
    projectionCostPerVisitorFallbackNote: text("No channel cost is available. Reinvestment uses {cost} per visitor from the model fallback (15% of revenue per visitor, minimum one selected-currency unit)."),
    projectionPlanningReferenceNote: text("Planner references are model assumptions for scenario comparison, not performance guarantees."),
    projectionZeroGrowthNote: text("The baseline assumes 0% automatic month-to-month growth across the selected horizon. Scenario changes and entered reinvestment are shown separately; these are planning scenarios, not forecasts or guarantees."),
    projectionStageColumn: text("Stage"),
    projectionCurrentCvrColumn: text("Current CVR"),
    projectionBenchmarkColumn: text("Planner reference"),
    projectionGapColumn: text("Gap"),
    projectionLiftColumn: text("+10% Lifts Revenue"),
    projectionLeverageColumn: text("Leverage"),
    projectionGapAbove: text("above"),
    projectionGapAt: text("at reference"),
    projectionGapBelow: text("below"),
    projectionMrrTitle: text("MRR Growth Forecast (12 Months)"),
    projectionMrrCaption: text("MRR growth forecast showing active subscribers and monthly recurring revenue"),
    projectionMonthColumn: text("Month"),
    projectionMonthsValue: text("{count} mo"),
    projectionActiveSubscribersColumn: text("Active Subscribers"),
    projectionMrrColumn: text("MRR"),
    projectionMonthValue: text("Month {month}"),
    projectionChartAria: text("Monthly revenue projection over {months} months"),
    projectionChartMonthLabel: text("M{month}"),

    // Contact and success screens
    contactStepHeading: text("Get your personalized funnel plan"),
    contactStepProgress: text("Step 7 of 7"),
    contactProgressAssembling: text("Assembling your funnel plan..."),
    contactProgressCalculating: text("Calculating projections..."),
    contactProgressGenerating: text("Generating your PDF..."),
    contactProgressAlmostReady: text("Almost ready..."),
    contactEmailInvalid: text("Please enter a valid email address (e.g. you@company.com)"),
    contactNameCounter: text("{count}/{max}"),
    contactCompanyCounter: text("{count}/{max}"),
    contactPrivacyConsent: text("I agree that SEOH may use my planner details to save the handoff and generate the PDF. Planner context is kept for 30 days. No email is sent automatically."),
    contactMarketingOptIn: text("Optional: email me occasional SEO tips and service updates. This is separate from the agreement above, is never required to get your plan, and you can unsubscribe at any time."),
    contactFormErrorLead: text("PDF generation failed - please try again or"),
    contactFormErrorSuffix: text("PDF generation failed - please try again or {supportLink}."),
    contactSupportLink: text("contact support"),
    contactProcessingButton: text("Processing your plan..."),
    successTemplateFunnelTitle: text("{template} Funnel"),
    successEditFunnelButton: text("Edit funnel"),
    successMonthlyVisitors: text("Monthly visitors"),
    successFinalStage: text("Final-stage / mo"),
    successEstimatedYearOneRevenue: text("Estimated monthly revenue at month {month}"),

    // PDF export
    pdfEyebrow: text("Funnel Plan"),
    pdfGeneratedLabel: text("Generated {date}"),
    pdfEstimateOnlyDisclaimer: text(
      "Planning estimate, not a guarantee. Every figure in this document is a modelled projection based on the inputs and assumptions you entered. It is not a quote, an offer, or a price we have agreed, and actual results may differ materially.",
    ),
    pdfFooter: text("Generated by SEOH.com - Confidential"),
    pdfAudience: text("Audience"),
    pdfStages: text("Stages"),
    pdfChannels: text("Channels"),
    pdfMonthlyVisitors: text("Monthly Visitors"),
    pdfRegionalSettingsTitle: text("Regional Settings"),
    pdfRegion: text("Region"),
    pdfCurrency: text("Currency"),
    pdfTaxAssumption: text("Tax assumption"),
    pdfMerchantFeeAssumption: text("Merchant fee assumption"),
    pdfExecutiveSummaryTitle: text("Executive Summary"),
    pdfRulesRecommendationsTitle: text("Rules-Based Recommendations"),
    pdfPlanVsActualTitle: text("Plan vs Actual Warnings"),
    pdfCapacityTitle: text("Capacity and Delay"),
    pdfCrmPdfStatus: text("Status: handoff details saved and branded PDF generated. No automatic email was sent."),
    pdfTrafficChannelMix: text("Traffic Channel Mix"),
    pdfVisitorsPerMonth: text("Visitors / mo"),
    pdfEstimatedCostPerMonth: text("Est. Cost / mo"),
    pdfScenarioComparison: text("Scenario comparison at month {month}"),
    pdfProjectionTitle: text("Projection through month {month}"),
    pdfBaselineRevenue: text("Baseline revenue / month"),
    pdfMonth12TargetRevenue: text("Target monthly revenue at month {month}"),
    pdfCumulativeUplift: text("Cumulative uplift over months 1 to {month}"),
    pdfRevenuePnlTitle: text("Revenue & P&L Analysis"),
    pdfProfitLossTitle: text("Profit & Loss"),
    pdfLineItem: text("Line Item"),
    pdfAmount: text("Amount"),
    pdfKeyPerformanceIndicators: text("Key Performance Indicators"),
    pdfProductSummary: text("Product Summary"),
    pdfProduct: text("Product"),
    pdfType: text("Type"),
    pdfPrice: text("Price"),
    pdfRefundPercent: text("Refund %"),
    pdfLtv: text("LTV"),
    pdfLeverageAssumptionsTitle: text("Leverage Analysis & Assumptions"),
    pdfSensitivityTitle: text("CVR Sensitivity Analysis"),
    pdfCvr: text("CVR"),
    pdfGap: text("Gap"),
    pdfGapAbove: text("Above"),
    pdfGapAt: text("At"),
    pdfGapBelow: text("Below"),
    pdfImpact: text("+10% Impact"),
    pdfNoStageData: text("No stage data available"),
    pdfProjectionAssumptions: text("Projection Assumptions"),
    pdfAssumption: text("Assumption"),
    pdfValue: text("Value"),
    pdfConfidence: text("Confidence"),
    pdfTotalMonthlyVisitors: text("Total Monthly Visitors"),
    pdfTrafficChannels: text("Traffic Channels"),
    pdfChannelsSet: text("{count} channels set"),
    pdfZeroChannels: text("0 channels"),
    pdfStageCvrs: text("Stage CVRs"),
    pdfProductPrice: text("Product Price"),
    pdfRefundRate: text("Refund Rate"),
    pdfMerchantFee: text("Merchant Fee"),
    pdfChurnRate: text("Churn Rate"),
    pdfHighConfidence: text("High"),
    pdfMediumConfidence: text("Medium"),
    pdfLowConfidence: text("Low"),
    pdfFunnelStagesTitle: text("Funnel Stages"),
    pdfStage: text("Stage"),
    pdfVolumeIn: text("Volume In"),
    pdfVolumeOut: text("Volume Out"),

    // Channel groups
    "channelGroups.searchOrganic": text("Search Organic"),
    "channelGroups.searchPaid": text("Search Paid"),
    "channelGroups.socialOrganic": text("Social Organic"),
    "channelGroups.socialPaid": text("Social Paid"),
    "channelGroups.video": text("Video"),
    "channelGroups.emailAndMessaging": text("Email & Messaging"),
    "channelGroups.contentAndPR": text("Content & PR"),
    "channelGroups.partnerships": text("Partnerships"),
    "channelGroups.displayAndProgrammatic": text("Display & Programmatic"),
    "channelGroups.retailMedia": text("Retail Media"),
    "channelGroups.aiSearchAEO": text("AI Search / AEO"),
    "channelGroups.printAndBroadcast": text("Print & Broadcast"),
    "channelGroups.retargeting": text("Retargeting"),
    "channelGroups.other": text("Other"),

    // Acquisition modes
    "acquisitionModes.organic": text("Organic"),
    "acquisitionModes.cpc": text("CPC (paid per click)"),
    "acquisitionModes.cpm": text("CPM (paid per impression)"),
    "acquisitionModes.cpv": text("CPV (paid per view)"),
    "acquisitionModes.flat_fee": text("Flat monthly fee"),
    "acquisitionModes.cpa": text("CPA (paid per customer)"),
    "acquisitionModes.cost_per_lead": text("CPL (paid per lead)"),
    "acquisitionModes.revenue_share": text("Revenue share"),
    "acquisitionModes.labor_time": text("Labor / time cost"),
    "acquisitionModes.organic_unknown": text("Organic / unknown cost"),

    // Stage type labels used by canvas, previews, and PDF
    "stageTypes.blog-post.label": text("Blog Post"),
    "stageTypes.landing-page.label": text("Landing Page"),
    "stageTypes.opt-in-page.label": text("Opt-In Page"),
    "stageTypes.email-sequence.label": text("Email Sequence"),
    "stageTypes.discovery-call.label": text("Discovery Call"),
    "stageTypes.proposal.label": text("Proposal"),
    "stageTypes.sales-page.label": text("Sales Page"),
    "stageTypes.order-form.label": text("Order Form"),
    "stageTypes.upsell.label": text("Upsell"),
    "stageTypes.webinar.label": text("Webinar"),
    "stageTypes.share-page.label": text("Share / Viral Page"),
    "stageTypes.application-page.label": text("Application / Survey Page"),
    "stageTypes.application.label": text("Application"),
    "stageTypes.retainer-close.label": text("Retainer Close"),
    "stageTypes.custom.label": text("Custom Stage"),

    // Product, price, and guarantee labels
    "productTypes.online_course": text("Online Course"),
    "productTypes.saas": text("SaaS"),
    "productTypes.membership": text("Membership"),
    "productTypes.digital_download": text("Digital Download"),
    "productTypes.coaching": text("Coaching"),
    "productTypes.done_for_you": text("Done-For-You"),
    "productTypes.agency_retainer": text("Agency Retainer"),
    "productTypes.physical_product": text("Physical Product"),
    "productTypes.supplement": text("Supplement"),
    "productTypes.affiliate": text("Affiliate"),
    "productTypes.high_ticket": text("High Ticket"),
    "productTypes.low_ticket": text("Low Ticket"),
    "productTypes.free_plus_shipping": text("Free + Shipping"),
    "productTypes.event": text("Event"),
    "productTypes.paid_newsletter": text("Paid Newsletter"),
    "productTypes.paid_community": text("Paid Community"),
    "productTypes.software_plugin": text("Software Plugin"),
    "productTypes.mentorship": text("Mentorship"),
    "productTypes.subscription_box": text("Subscription Box"),
    "productTypes.usage_based_api": text("Usage-Based API"),
    "productTypes.seat_based_saas": text("Seat-Based SaaS"),
    "productTypes.mobile_app": text("Mobile App"),
    "productTypes.cohort_course": text("Cohort Course"),
    "productTypes.franchise": text("Franchise"),
    "productTypes.local_service": text("Local Service"),
    "productTypes.ai_agent": text("AI Agent"),
    "productTypes.other": text("Other"),
    "priceTypes.one_time": text("One-Time"),
    "priceTypes.monthly": text("Monthly"),
    "priceTypes.yearly": text("Yearly"),
    "priceTypes.usage_based": text("Usage-Based"),
    "priceTypes.seat_based": text("Seat-Based"),
    "priceTypes.payment_plan": text("Payment Plan"),
    "guarantees.none": text("No Guarantee"),
    "guarantees.money_back_30": text("30-Day Money Back"),
    "guarantees.money_back_60": text("60-Day Money Back"),
    "guarantees.satisfaction": text("Satisfaction Guarantee"),
    "guarantees.results": text("Results Guarantee"),

    // Channel labels
    "channels.google_organic.label": text("Google Organic"),
    "channels.bing_organic.label": text("Bing Organic"),
    "channels.other_search.label": text("Other Search / AI"),
    "channels.yahoo_organic.label": text("Yahoo Organic"),
    "channels.yandex_organic.label": text("Yandex Organic"),
    "channels.google_ads.label": text("Google Ads"),
    "channels.bing_ads.label": text("Bing Ads"),
    "channels.ppc_source.label": text("PPC (Other)"),
    "channels.yahoo_ads.label": text("Yahoo Ads"),
    "channels.yandex_ads.label": text("Yandex Ads"),
    "channels.facebook_organic.label": text("Facebook Organic"),
    "channels.instagram.label": text("Instagram"),
    "channels.linkedin.label": text("LinkedIn Organic"),
    "channels.twitter.label": text("X / Twitter"),
    "channels.pinterest.label": text("Pinterest Organic"),
    "channels.tiktok.label": text("TikTok Organic"),
    "channels.snapchat.label": text("Snapchat Organic"),
    "channels.youtube.label": text("YouTube Organic"),
    "channels.vimeo.label": text("Vimeo"),
    "channels.vk_organic.label": text("VK Organic"),
    "channels.whatsapp.label": text("WhatsApp"),
    "channels.facebook_ads.label": text("Facebook / Instagram Ads"),
    "channels.linkedin_ads.label": text("LinkedIn Ads"),
    "channels.twitter_ads.label": text("X / Twitter Ads"),
    "channels.pinterest_ads.label": text("Pinterest Ads"),
    "channels.tiktok_ads.label": text("TikTok Ads"),
    "channels.snapchat_ads.label": text("Snapchat Ads"),
    "channels.vk_ads.label": text("VK Ads"),
    "channels.whatsapp_ads.label": text("WhatsApp Ads"),
    "channels.youtube_ads.label": text("YouTube Ads"),
    "channels.video_ads.label": text("Video Ads (Other)"),
    "channels.email_newsletter.label": text("Email Newsletter"),
    "channels.email_ads.label": text("Email Ads (Solo/Paid)"),
    "channels.sms.label": text("SMS / Text"),
    "channels.blog_articles.label": text("Blog / Articles"),
    "channels.podcast.label": text("Podcast"),
    "channels.press_release.label": text("Press Release"),
    "channels.referral.label": text("Referral"),
    "channels.jv_partners.label": text("JV Partners"),
    "channels.affiliates.label": text("Affiliates"),
    "channels.cpa_network.label": text("CPA Network"),
    "channels.display_ads.label": text("Display Ads"),
    "channels.media_buys.label": text("Media Buys"),
    "channels.ppv_source.label": text("PPV / Pop Traffic"),
    "channels.direct_mail.label": text("Direct Mail"),
    "channels.magazine_ad.label": text("Magazine Ad"),
    "channels.newspaper_ad.label": text("Newspaper Ad"),
    "channels.radio_ad.label": text("Radio Ad"),
    "channels.tv_ad.label": text("Television Ad"),
    "channels.retargeting.label": text("Retargeting"),
    "channels.retargeting_meta.label": text("Meta Retargeting"),
    "channels.reddit_organic.label": text("Reddit Organic"),
    "channels.quora_organic.label": text("Quora Organic"),
    "channels.skool.label": text("Skool Community"),
    "channels.reddit_ads.label": text("Reddit Ads"),
    "channels.quora_ads.label": text("Quora Ads"),
    "channels.amazon_ads.label": text("Amazon Ads"),
    "channels.apple_search_ads.label": text("Apple Search Ads (ASA)"),
    "channels.ctv_ott.label": text("CTV / Connected TV"),
    "channels.taboola.label": text("Taboola / Outbrain (Native)"),
    "channels.dsp.label": text("Programmatic DSP (TTD/DV360)"),
    "channels.review_sites.label": text("Review Sites (G2 / Capterra)"),
    "channels.aso.label": text("App Store Organic (ASO)"),
    "channels.amazon_dsp.label": text("Amazon DSP"),
    "channels.walmart_connect.label": text("Walmart Connect"),
    "channels.target_roundel.label": text("Target Roundel"),
    "channels.instacart_ads.label": text("Instacart Ads"),
    "channels.chatgpt_search.label": text("ChatGPT / OpenAI Search"),
    "channels.perplexity_ai.label": text("Perplexity AI"),
    "channels.gemini_search.label": text("Google Gemini / AI Overview"),
    "channels.bing_copilot.label": text("Bing Copilot / Copilot Search"),
    "channels.custom.label": text("Custom / Other"),

    // NL-041 package adviser (step 3). Advisory copy only: nothing here may
    // describe a quote, an invoice, a checkout, or a committed price.
    packageAdviserTitle: text("Suggested plan for this funnel"),
    packageAdviserIntro: text("Based on the scope you have mapped so far. You can accept it, pick a different plan, or skip this entirely."),
    packageAdviserRecommended: text("Suggested plan: {tier}"),
    packageAdviserBandLabel: text("Indicative range"),
    packageAdviserAdvisoryNote: text("Indicative planning range only. It is not a quote, an offer, or a price we have agreed."),
    packageAdviserApproximate: text("Converted from US dollars at an indicative rate, so treat it as a rough guide."),
    packageAdviserReasonsLabel: text("Why this plan"),
    packageAdviserAccept: text("Use this suggestion"),
    packageAdviserOverrideLabel: text("Use a different plan"),
    packageAdviserOverridePlaceholder: text("Select a package tier"),
    packageAdviserSkip: text("Skip this step"),
    packageAdviserChange: text("Change"),
    packageAdviserAcceptedStatus: text("Planning with {tier}."),
    packageAdviserOverriddenStatus: text("You chose {tier} instead of the suggested {recommended}."),
    packageAdviserSkippedStatus: text("Skipped. No plan is attached to this funnel."),
    packageAdviserManualQuote: text("We cannot show an indicative range for this setup, so this one needs a person to scope it."),
    packageAdviserSectionAria: text("Package suggestion"),

    // NL-041 reason labels. Keyed by the shared rule's own reason vocabulary.
    "packageAdviserReason.baseline-scope": text("A single, straightforward offer"),
    "packageAdviserReason.service-count-growth": text("More than one offer to support"),
    "packageAdviserReason.service-count-enterprise": text("A wide set of offers to support"),
    "packageAdviserReason.addon-count-growth": text("Several add-ons selected"),
    "packageAdviserReason.addon-count-enterprise": text("Many add-ons selected"),
    "packageAdviserReason.complexity-growth": text("One factor that adds delivery complexity"),
    "packageAdviserReason.complexity-enterprise": text("Several factors that add delivery complexity"),
    "packageAdviserReason.package-override": text("You picked this plan yourself"),
    "packageAdviserReason.agency-audience": text("Agency audience"),
    "packageAdviserReason.adult-audience": text("Adult-industry compliance requirements"),
    "packageAdviserReason.franchise-audience": text("Franchise audience"),
    "packageAdviserReason.white-label-service": text("White-label delivery"),
    "packageAdviserReason.franchise-local-service": text("Multi-location local delivery"),
    "packageAdviserReason.compliance-addon": text("Compliance review included"),

    // NL-010 seasonal planning scenarios (step 6).
    seasonalScenariosTitle: text("Seasonal planning scenarios"),
    seasonalScenariosIntro: text("Plan for a slow month, a normal month, and a strong month by saying how much your visitor volume moves."),
    seasonalScenariosEnable: text("Add a seasonal planning assumption"),
    seasonalScenariosOwnedNote: text("These percentages are your own planning assumptions. We are not supplying market or seasonality data here."),
    seasonalScenariosDemandOnlyNote: text("Applied to monthly visitors only. Revenue moves only because visits move, never on its own."),
    seasonalScenarioLowerLabel: text("Slow month"),
    seasonalScenarioExpectedLabel: text("Normal month"),
    seasonalScenarioUpperLabel: text("Strong month"),
    seasonalScenarioPercentLabel: text("Change in monthly visitors (%)"),
    seasonalScenarioVisitorsLabel: text("Planned monthly visitors"),
    seasonalScenarioMultiplierLabel: text("Multiplier"),
    seasonalScenariosBlockedTitle: text("Check these assumptions"),
    seasonalScenariosSectionAria: text("Seasonal planning scenarios"),
    seasonalScenariosSourceDisabledNote: text("Market-sourced seasonal profiles stay switched off until a reviewed data source is approved."),

    // NL-014 calculation explanation (step 6).
    calculationExplanationTitle: text("How this estimate was worked out"),
    calculationExplanationIntro: text("A plain record of the method, assumptions, and anything limiting the result."),
    calculationExplanationSessionNote: text("Shown for this session only. It is not saved, not shared, and not included in your PDF."),
    calculationExplanationEstimateOnly: text("Planning estimate only. It is not a quote and commits nobody to anything."),
    calculationExplanationShow: text("Show the calculation detail"),
    calculationExplanationHide: text("Hide the calculation detail"),
    calculationExplanationMethodLabel: text("Method"),
    calculationExplanationPackageLabel: text("Plan suggestion"),
    calculationExplanationSeasonalLabel: text("Seasonal assumptions"),
    calculationExplanationRangeLabel: text("Planning range"),
    calculationExplanationBlockersLabel: text("What is limiting this estimate"),
    calculationExplanationNoBlockers: text("Nothing is currently limiting this estimate."),
    calculationExplanationMethodVersion: text("This estimate uses the current planner settings."),
    calculationExplanationRuleVersion: text("The suggested plan is {recommendedTier}."),
    calculationExplanationSeasonalMethod: text("Your seasonal assumptions are applied to estimated monthly visitors."),
    calculationExplanationRangeRow: text("{scenario}: {monthlyVisitors} monthly visitors"),
    calculationExplanationReceiptRow: text("The calculation uses the information entered in this session and standard rounding."),
    calculationExplanationComponentRow: text("{component}: {status}."),
    calculationExplanationBandRow: text("Indicative range {pricingBand}."),
    calculationExplanationMissingRow: text("Still missing: {missing}."),
    calculationExplanationSeasonalScenarioRow: text("{scenario}: {percent}% change in planned visitors."),
    calculationExplanationNotRecorded: text("not recorded"),
    calculationExplanationUnavailableDetail: text("Additional detail is unavailable."),
    calculationExplanationSectionAria: text("Calculation explanation"),

    // Calculation component names and statuses, keyed by the NL-014 contract tokens.
    "explanationComponent.seasonalDemand": text("Seasonal demand"),
    "explanationComponent.priceEvidence": text("Price evidence"),
    "explanationComponent.packageRecommendation": text("Plan suggestion"),
    "explanationComponentStatus.used": text("used in this estimate"),
    "explanationComponentStatus.not-used": text("not used in this estimate"),

    // Missing-information labels, keyed by the NL-041 rule tokens.
    "explanationMissing.market": text("region"),
    "explanationMissing.currency": text("currency"),
    "explanationMissing.pricing-snapshot": text("indicative pricing"),
    "explanationMissing.pricing-snapshot-version": text("pricing snapshot version"),
    "explanationMissing.pricing-band": text("indicative range"),

    // Blocker labels, keyed by the machine tokens the contracts emit.
    "explanationBlocker.currency-mismatch": text("The indicative range is published in a different currency to the one you picked."),
    "explanationBlocker.no-pricing-snapshot": text("No indicative pricing is available for this page."),
    "explanationBlocker.missing-region": text("No region is set."),
    "explanationBlocker.missing-currency": text("No currency is set."),
    "explanationBlocker.missing-assumption": text("One of the three seasonal percentages is empty."),
    "explanationBlocker.assumption-out-of-range": text("A seasonal percentage is outside the allowed range."),
    "explanationBlocker.assumption-order-invalid": text("The slow month must not be higher than the normal month, and the normal month must not be higher than the strong month."),
    "explanationBlocker.invalid-baseline-visitors": text("The monthly visitor figure is not a whole number."),
    "explanationBlocker.seasonal-multiplier-already-applied": text("A seasonal multiplier has already been applied upstream."),
    "explanationBlocker.revenue-multiplier-not-supported": text("Seasonality is not applied to revenue."),
    "explanationBlocker.package-recommendation-component-requires-manual-quote": text("The plan suggestion needs a person to scope it, so it is not counted as an input."),
    "explanationBlocker.seasonal-demand-component-not-ready": text("The seasonal calculation is not ready, so it is not counted as an input."),
    "explanationBlocker.price-evidence-component-not-ready": text("The pricing evidence is not ready, so it is not counted as an input."),
  },
});

export const funnelPlannerEntry = applyLocaleOverrides([
  baseFunnelPlannerEntry,
], CONTENT_LOCALE_OVERRIDES)[0] as typeof baseFunnelPlannerEntry;

export function getFunnelPlannerContent(locale: AppLocale = DEFAULT_LOCALE) {
  return resolveContent({
    entry: funnelPlannerEntry,
    locale,
    persona: "none",
    surface: "canonical",
    indexable: true,
    allowPreviewFallback: false,
  }).content;
}

export type FunnelPlannerContent = ReturnType<typeof getFunnelPlannerContent>;
